import { FilterTypes } from '../../../../modules/sortAndFilter/workers/sortAndFilter';
import FilterSet from './FilterSet';

export interface FilterTag {
    id: string;
    label: string;
    onClick: () => void;
}

/**
 * Base filter class that contains the logic for managing filter keys and provides helper functions for the
 * UI.
 */
class Filter<T> {
    readonly type = '' as FilterTypes['type']; // The type of filter to be applied.
    readonly keys = new Set() as FilterSet<T>; // The values to filter against.
    private urlKey = '' as string; // The unique key to add and retrieve filters from the URL with.
    private windowAvailable = typeof window !== 'undefined';

    constructor(type: FilterTypes['type'], urlKey: string) {
        this.type = type;
        this.urlKey = urlKey;
        this.keys = new FilterSet(type);
    }

    /**
     * Formats the keys for the filter from the URL.
     */
    protected formatKeysFromUrl = (urlValue: string) => urlValue.split(',') as T[];

    /**
     * Retrieves the keys for the filter from the URL.
     */
    protected setKeysFromUrl = () => {
        if (!this.windowAvailable) return;

        const params = new URLSearchParams(window.location.search);
        const urlParam = `filter.${this.urlKey}.${this.type}`;

        if (params.has(urlParam) && params.get(urlParam)) {
            const urlValues = this.formatKeysFromUrl(params.get(urlParam)!);
            urlValues.forEach((val) => this.keys.add(val));
        }
    };

    /**
     * Formats the keys for the filter to be added to the URL.
     */
    formatKeysForUrl = () => Array.from(this.keys).join(',');

    /**
     * Adds the keys in the filter to the URL.
     */
    readonly addKeysToUrl = () => {
        if (!this.windowAvailable) return;

        const params = new URLSearchParams(window.location.search);
        const urlParam = `filter.${this.urlKey}.${this.type}`;

        params.delete(urlParam);

        if (this.keys.size) {
            params.set(urlParam, this.formatKeysForUrl());
        }

        const url = window.location.hash ? `?${params.toString()}${window.location.hash}` : `?${params.toString()}`;

        window.history.replaceState(null, '', url);
    };

    /**
     * Loops through the filter keys and formats them for the filter type.
     */
    protected formatFilterTypeKeys = () => Array.from(this.keys) as FilterTypes['keys'];

    /**
     * Creates filter types from the type and keys in the filter.
     */
    readonly filterTypes = (): FilterTypes[] => {
        if (!this.keys.size) return [];
        return [
            {
                type: this.type,
                keys: this.formatFilterTypeKeys(),
            },
        ];
    };

    /**
     * Formats a key to a tag label for the UI.
     */
    protected formatTagLabel = (key: T) => key as string;

    /**
     * Creates filter tags from the keys in this class for the UI.
     */
    readonly tags = (): {
        id: string;
        label: string;
        onClick: () => void;
    }[] =>
        Array.from(this.keys).map((key) => {
            const label = this.formatTagLabel(key);

            return {
                id: `${this.type}-${label}`,
                label,
                onClick: () => this.keys.delete(key),
            };
        });
}

export default Filter;
